// /api-keys — developer API key management.
// Plaintext token is shown ONCE on creation; after that only the prefix.

import { useEffect, useState } from 'react';
import { ApiError, developerKeys, type CreatedDeveloperKey, type DeveloperKey } from '../api.js';

export function ApiKeysPage() {
  const [list, setList] = useState<DeveloperKey[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [revealedKey, setRevealedKey] = useState<CreatedDeveloperKey | null>(null);

  async function refresh() {
    setError(null);
    try {
      const r = await developerKeys.list();
      setList(r.keys);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load.');
      setList([]);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleCreate() {
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await developerKeys.create({ name: name.trim() });
      setRevealedKey(r.key);
      setName('');
      setCreateOpen(false);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Create failed.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRevoke(id: string, name: string) {
    if (!confirm(`Revoke "${name}"? Any application using this key will start failing immediately.`)) return;
    try {
      await developerKeys.revoke(id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Revoke failed.');
    }
  }

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl mb-1">API keys</h1>
          <p className="text-muted text-sm">
            Use these to call ScriptVoice from your own code via <span className="font-mono">/api/v1/*</span>.
            Each request is billed against your account.
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="bg-ink text-parchment px-4 py-2 rounded-md hover:bg-ink/90 transition-colors text-sm"
        >
          New API key
        </button>
      </div>

      {error && (
        <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {revealedKey && (
        <div className="border-2 border-accent rounded-lg bg-accent/5 px-5 py-4 mb-4">
          <h2 className="font-serif text-lg mb-2">Save this key now — it will not be shown again.</h2>
          <div className="bg-white border border-soft rounded p-3 font-mono text-sm break-all mb-3">
            {revealedKey.token}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                void navigator.clipboard.writeText(revealedKey.token);
              }}
              className="text-xs bg-ink text-parchment px-3 py-1.5 rounded"
            >
              Copy to clipboard
            </button>
            <button
              onClick={() => setRevealedKey(null)}
              className="text-xs text-muted hover:text-ink px-3 py-1.5"
            >
              I've saved it, dismiss
            </button>
          </div>
        </div>
      )}

      {createOpen && (
        <div className="border border-soft rounded-lg bg-white px-5 py-5 mb-4">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-serif text-xl">Name this key</h2>
            <button onClick={() => setCreateOpen(false)} className="text-xs text-muted hover:text-ink">
              Cancel
            </button>
          </div>
          <div className="space-y-3">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='e.g. "Production backend"'
              maxLength={80}
              autoFocus
              className="w-full px-3 py-2 bg-parchment/60 border border-soft rounded"
            />
            <button
              onClick={handleCreate}
              disabled={submitting || !name.trim()}
              className="bg-accent text-parchment px-4 py-2 rounded text-sm hover:bg-accent/90 disabled:opacity-40"
            >
              {submitting ? 'Creating…' : 'Create key'}
            </button>
          </div>
        </div>
      )}

      {list === null ? (
        <p className="text-muted text-sm">loading…</p>
      ) : list.length === 0 ? (
        <div className="border border-soft rounded-lg p-12 text-center">
          <p className="text-muted">No API keys yet.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {list.map((k) => (
            <li
              key={k.id}
              className={`border border-soft rounded-lg px-5 py-4 bg-white ${k.revoked_at ? 'opacity-60' : ''}`}
            >
              <div className="flex items-baseline justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="font-serif text-lg truncate">{k.name}</h2>
                    {k.revoked_at && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-soft/60 text-muted rounded">
                        revoked
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted/80 mt-1 font-mono">
                    {k.prefix}··· · {k.request_count.toLocaleString()} requests · created{' '}
                    {new Date(k.created_at).toLocaleDateString()}
                  </div>
                  {k.last_used_at && (
                    <div className="text-xs text-muted/70 mt-0.5">
                      last used {new Date(k.last_used_at).toLocaleString()}
                    </div>
                  )}
                </div>
                {!k.revoked_at && (
                  <button
                    onClick={() => handleRevoke(k.id, k.name)}
                    className="text-xs text-muted hover:text-accent px-2 py-1 shrink-0"
                  >
                    Revoke
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
